import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { useState } from 'react';
import { useSearchParams } from 'react-router';

import { Button, Drawer, DrawerHeader, Skeleton, cn } from '@/design-system';
import {
    ORDER_FILTERS,
    isOrderFilterKey,
    orderAccessFor,
    orderApi,
    orderKeys,
    type Order,
    type OrderSort,
} from '@/entities/order';
import { errorMessage, useSessionStore } from '@/shared/api';
import { useBreakpoint, usePreference } from '@/shared/lib';
import { ErrorState, Pager, Screen, ScreenHeader } from '@/shared/ui';

import { AttentionLine } from './components/AttentionLine';
import { FilterBar } from './components/FilterBar';
import { NewOrderForm } from './components/NewOrderForm';
import { OrderCards } from './components/OrderCards';
import { OrderDetailBar } from './components/OrderDetailBar';
import { OrderTable } from './components/OrderTable';
import { inspectionsCopy } from './copy';
import { downloadCsv, ordersToCsv } from './export';
import { useDebounced, useInspectionTypes, useOrderCounts, useOrderList } from './hooks';
import { NEW_PARAM, STATUS_PARAM } from './listLink';

const PER_PAGE = 25;

/**
 * Scherm 5 - Keuringen: de werklijst van kantoor.
 *
 * Filteren, zoeken, sorteren en bladeren doet de API; de tellers op de pillen
 * volgen de zoekterm en de dienst. Het statusfilter staat in het adres
 * (`?status=nieuw`), zodat de regel onder de kop en de Planning ernaar kunnen
 * linken. Eén rij is geselecteerd; de detailbalk
 * eronder toont wat je er nu mee kunt, en elke knop daar doet iets echts.
 */
export function InspectionsPage() {
    const { isNarrow, isPhone } = useBreakpoint();
    const [searchParams, setSearchParams] = useSearchParams();
    const session = useSessionStore((state) => state.session);
    const access = orderAccessFor(session);

    const [search, setSearch] = useState('');
    const [service, setService] = useState('');
    const [sort, setSort] = useState<OrderSort>('opname');
    const [page, setPage] = useState(1);
    const [selectedUuid, setSelectedUuid] = useState<string | null>(null);
    const [collapsed, setCollapsed] = usePreference('keuringen-detail', false);
    const [panelOpen, setPanelOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [notice, setNotice] = useState<{ tone: 'done' | 'problem'; message: string } | null>(
        null,
    );

    const statusParam = searchParams.get(STATUS_PARAM) ?? '';
    const status = isOrderFilterKey(statusParam) ? statusParam : 'alle';
    const creating = access.manage && searchParams.get(NEW_PARAM) !== null;
    const settledSearch = useDebounced(search);
    const query = { status, search: settledSearch, service, sort, page, perPage: PER_PAGE };

    const list = useOrderList(query);
    const counts = useOrderCounts({ search: settledSearch, service });
    const inspectionTypes = useInspectionTypes();

    const rows = list.data?.items ?? [];
    const inRows = rows.find((order) => order.uuid === selectedUuid);
    const detail = useQuery({
        queryKey: orderKeys.detail(selectedUuid ?? ''),
        queryFn: () => orderApi.show(selectedUuid ?? ''),
        enabled: selectedUuid !== null,
        initialData: inRows,
        staleTime: 15_000,
    });
    // Geen selectie, geen balk: een lege detailbalk kost alleen maar hoogte.
    const selected = selectedUuid ? (detail.data ?? inRows ?? null) : null;

    const total = counts.data?.alle ?? list.data?.meta?.total ?? rows.length;
    // Een filter zonder eigen pil (vanaf de Planning) telt de API niet mee bij de
    // pillen; zijn aantal is dat van de lijst zelf.
    const pillCounts =
        counts.data && !ORDER_FILTERS.some((filter) => filter.key === status) && list.data?.meta
            ? { ...counts.data, [status]: list.data.meta.total }
            : counts.data;

    function changeFilter(update: () => void) {
        update();
        setPage(1);
    }

    function setStatus(key: string) {
        setSearchParams(
            (current) => {
                const next = new URLSearchParams(current);

                if (key === 'alle') {
                    next.delete(STATUS_PARAM);
                } else {
                    next.set(STATUS_PARAM, key);
                }

                return next;
            },
            { replace: true },
        );
    }

    function setCreating(open: boolean) {
        const next = new URLSearchParams(searchParams);

        if (open) {
            next.set(NEW_PARAM, '1');
        } else {
            next.delete(NEW_PARAM);
        }

        setSearchParams(next, { replace: true });
    }

    function select(order: Order) {
        setSelectedUuid(order.uuid);
        setNotice(null);

        if (isNarrow) {
            setPanelOpen(true);
        }
    }

    async function exportList() {
        setExporting(true);
        setNotice(null);

        try {
            const orders = await orderApi.all({ status, search: settledSearch, service, sort });

            downloadCsv(
                inspectionsCopy.exportFileName(format(new Date(), 'yyyy-MM-dd')),
                ordersToCsv(orders, { money: access.money }),
            );
        } catch (error) {
            setNotice({
                tone: 'problem',
                message: `${inspectionsCopy.exportFailed}: ${errorMessage(error)}`,
            });
        } finally {
            setExporting(false);
        }
    }

    return (
        <Screen>
            <ScreenHeader
                variant="mid"
                eyebrow={
                    counts.data ? inspectionsCopy.eyebrow(total) : inspectionsCopy.eyebrowLoading
                }
                title={inspectionsCopy.title}
                actions={
                    <>
                        <Button
                            variant="onDark"
                            size="compact"
                            icon="download"
                            disabled={exporting || rows.length === 0}
                            onClick={() => void exportList()}
                        >
                            {exporting ? inspectionsCopy.exporting : inspectionsCopy.export}
                        </Button>
                        {access.manage ? (
                            <Button
                                variant="primary"
                                size="compact"
                                icon="add"
                                onClick={() => setCreating(true)}
                            >
                                {inspectionsCopy.newInspection}
                            </Button>
                        ) : null}
                    </>
                }
            />

            <AttentionLine
                counts={counts.data}
                onStatusChange={(key) => changeFilter(() => setStatus(key))}
            />

            <FilterBar
                counts={pillCounts}
                services={inspectionTypes.data ?? []}
                status={status}
                search={search}
                service={service}
                sort={sort}
                onStatusChange={(key) => changeFilter(() => setStatus(key))}
                onSearchChange={(value) => changeFilter(() => setSearch(value))}
                onServiceChange={(value) => changeFilter(() => setService(value))}
                onSortChange={(value) => changeFilter(() => setSort(value))}
            />

            {/* Eén flex-kolom: de lijst krijgt de resthoogte en scrollt, de
                detailbalk staat er als `auto`-rij onder en blijft dus staan. */}
            <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--surface-page)]">
                {notice ? (
                    <p
                        role={notice.tone === 'done' ? 'status' : 'alert'}
                        className={cn(
                            'm-0 border-b border-[var(--border-default)] bg-[var(--surface-card)] px-6 py-[10px] text-[13px]',
                            notice.tone === 'done'
                                ? 'text-[var(--accent-dark)]'
                                : 'text-[var(--danger)]',
                        )}
                    >
                        {notice.message}
                    </p>
                ) : null}

                <div className="flex min-h-[120px] flex-1 flex-col overflow-auto bg-[var(--surface-card)]">
                    {list.isError ? (
                        <div className="p-6">
                            <ErrorState
                                error={list.error}
                                title={inspectionsCopy.errorTitle}
                                onRetry={() => void list.refetch()}
                            />
                        </div>
                    ) : list.isPending ? (
                        <div className="flex flex-col gap-2 p-6">
                            <Skeleton height={16} width="40%" pulse label={inspectionsCopy.title} />
                            <Skeleton height={16} width="70%" pulse />
                            <Skeleton height={16} width="55%" pulse />
                        </div>
                    ) : rows.length === 0 ? (
                        <p className="m-0 p-6 text-[13px] text-[var(--text-tertiary-safe)]">
                            {settledSearch.trim() !== ''
                                ? inspectionsCopy.emptySearch(settledSearch.trim())
                                : status !== 'alle' || service !== ''
                                  ? inspectionsCopy.empty
                                  : access.manage
                                    ? inspectionsCopy.emptyAll
                                    : inspectionsCopy.emptyAllReadOnly}
                        </p>
                    ) : (
                        <>
                            {/* Op een telefoon past geen tabel van 1152 px: daar is elke
                                keuring een kaart met haar eigen knop, in één kolom. */}
                            <div className="flex-1">
                                {isPhone ? (
                                    <OrderCards
                                        orders={rows}
                                        selectedUuid={selected?.uuid ?? null}
                                        access={access}
                                        onSelect={select}
                                    />
                                ) : (
                                    <OrderTable
                                        orders={rows}
                                        selectedUuid={selected?.uuid ?? null}
                                        access={access}
                                        onSelect={select}
                                    />
                                )}
                            </div>
                            <Pager
                                meta={list.data?.meta ?? null}
                                noun={inspectionsCopy.pagerNoun}
                                busy={list.isFetching}
                                onPage={setPage}
                            />
                        </>
                    )}
                </div>

                {selected && !isNarrow ? (
                    <OrderDetailBar
                        key={selected.uuid}
                        order={selected}
                        access={access}
                        collapsed={collapsed}
                        onToggleCollapsed={setCollapsed}
                    />
                ) : null}
            </div>

            {/* Onder 1000 px containerbreedte wordt de balk een uitschuifpaneel,
                net als de boom en de bevindingen in de opname. */}
            {isNarrow && selected ? (
                <Drawer
                    open={panelOpen}
                    onClose={() => setPanelOpen(false)}
                    side="right"
                    width={380}
                    title={inspectionsCopy.detailLabel}
                >
                    <OrderDetailBar
                        key={selected.uuid}
                        order={selected}
                        access={access}
                        layout="panel"
                        onClose={() => setPanelOpen(false)}
                    />
                </Drawer>
            ) : null}

            {/* Een laag over de hele app, niet binnen de lijst: de kopbalk en de
                filterbalk liggen eronder en zijn zolang niet te bedienen. */}
            <Drawer
                open={creating}
                onClose={() => setCreating(false)}
                side="right"
                width={520}
                title={inspectionsCopy.newTitle}
            >
                <DrawerHeader
                    icon="inspections"
                    title={inspectionsCopy.newTitle}
                    description={inspectionsCopy.newIntro}
                    onClose={() => setCreating(false)}
                    closeLabel={inspectionsCopy.newClose}
                />
                <NewOrderForm
                    inspectionTypes={inspectionTypes.data ?? []}
                    onCancel={() => setCreating(false)}
                    onCreated={(order) => {
                        setCreating(false);
                        setSelectedUuid(order.uuid);
                        setNotice({
                            tone: 'done',
                            message: inspectionsCopy.newCreated(order.number),
                        });
                    }}
                />
            </Drawer>
        </Screen>
    );
}
